
// client/src/components/DashboardTaskList.jsx
// Embedded task list for the Dashboard with role-based access control.
// - Operators: see only their own tasks
// - Managers/Admins/Owners: see all tasks (configurable via tenant settings)
//
// Follows the same embedded-list pattern as ScheduledPostsList and ScheduledEventsList.

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getSessionLocal, sessionRequestConfig } from "../services/sessionLocal";
import { useTranslation } from "react-i18next";
import { FaEye } from "react-icons/fa";
import "./DashboardTaskList.css";

const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

/** Max items to display in embedded (dashboard) mode */
const EMBEDDED_LIMIT = 5;

/** Priority sort order (urgent first) */
const PRIORITY_ORDER = { urgent: 0, high: 1, medium: 2, low: 3 };

/** Human-friendly date for display */
function formatDateShort(value) {
  if (!value) return "\u2014";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return String(value);
  }
}

/** Badge class for task status */
function statusBadgeClass(status) {
  switch ((status || "").toLowerCase()) {
    case "completed":  return "bg-success";
    case "in-progress": return "bg-info";
    case "blocked":    return "bg-danger";
    default:           return "bg-secondary"; // pending
  }
}

/** Badge class for task priority */
function priorityBadgeClass(priority) {
  switch ((priority || "").toLowerCase()) {
    case "urgent": return "bg-danger";
    case "high":   return "bg-warning";
    case "medium": return "bg-info";
    default:       return "bg-secondary"; // low
  }
}

export default function DashboardTaskList({ tenantId, embedded = false }) {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const session = getSessionLocal();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Read dashboard config from localStorage (set by TenantSettings)
  const getDashboardConfig = useCallback(() => {
    try {
      const raw = localStorage.getItem("dashboardTaskConfig");
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    // Defaults: managers see all tasks, operators see own
    return {
      taskListEnabled: true,
      managersCanSeeAllTasks: true,
      taskListLimit: EMBEDDED_LIMIT,
    };
  }, []);

  const config = getDashboardConfig();
  const effectiveTenantId = tenantId || tenant;

  // Determine visibility: admins/managers see all, operators see own
  const role = (session.role || "").toLowerCase();
  const isManagerOrAbove = ["owner", "admin", "manager"].includes(role);
  const canSeeAllTasks = isManagerOrAbove && config.managersCanSeeAllTasks;

  const fetchTasks = useCallback(async () => {
    if (!config.taskListEnabled) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const cfg = sessionRequestConfig();

      // Build query params — always filter to current user's tasks
      const params = {};
      params.username = session.username;
      // Fetch upcoming/active tasks (non-completed by default)
      params.status = "active"; // backend should interpret: pending, in-progress, blocked

      const res = await axios.get(
        `${baseUrl}/api/events/${effectiveTenantId}/tasks`,
        { ...cfg, params: { ...cfg.params, ...params } }
      );

      let fetched = [];
      if (res.data?.success) {
        fetched = res.data.tasks || res.data.data || [];
      } else if (Array.isArray(res.data)) {
        fetched = res.data;
      }

      // Sort: urgent/high first, then by due date
      fetched.sort((a, b) => {
        const pa = PRIORITY_ORDER[a.priority?.toLowerCase()] ?? 99;
        const pb = PRIORITY_ORDER[b.priority?.toLowerCase()] ?? 99;
        if (pa !== pb) return pa - pb;
        return new Date(a.taskdate || a.dueDate || 0) - new Date(b.taskdate || b.dueDate || 0);
      });

      setTasks(fetched);
    } catch (err) {
      console.error("Error loading dashboard tasks:", err);
      setError("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [effectiveTenantId, session.username, config.taskListEnabled]);

  useEffect(() => {
    if (effectiveTenantId) fetchTasks();
  }, [effectiveTenantId, fetchTasks]);

  // If task list is disabled in settings, render nothing
  if (!config.taskListEnabled) return null;

  const displayTasks = embedded ? tasks.slice(0, config.taskListLimit || EMBEDDED_LIMIT) : tasks;
  const hasMore = embedded && tasks.length > displayTasks.length;

  const handleTaskClick = (task) => {
    if (task.eventid) {
      const path = task.eventitemid
        ? `/${tenant || effectiveTenantId}/${task.eventid}/${task.eventitemid}/eventItemDetail`
        : `/${tenant || effectiveTenantId}/${task.eventid}/eventDetail`;
      navigate(path);
    }
  };

  // ── Embedded render ──
  if (embedded) {
    return (
      <div className="dashboard-tasklist-theme">
        <div className="dashboard-tasklist-container">
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <h6 style={{ margin: 0, fontWeight: 700, color: "#fff" }}>
              {t("dashboard.myTasksTitle", "My Tasks")}
            </h6>
            {tasks.length > 0 && (
              <span
                className="view-all-link"
                onClick={() => navigate(`/${tenant || effectiveTenantId}/tasks`)}
                style={{
                  fontSize: "0.78rem",
                  color: "#ff6800",
                  cursor: "pointer",
                }}
              >
                {t("common.viewAll", "View All")} ({tasks.length})
              </span>
            )}
          </div>

          {/* Loading */}
          {loading && (
            <div className="text-center" style={{ padding: "1rem 0" }}>
              <small className="text-gray-400">{t("common.loading", "Loading...")}</small>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="text-center" style={{ padding: "1rem 0" }}>
              <small style={{ color: "#dc3545" }}>{error}</small>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && displayTasks.length === 0 && (
            <div className="text-center" style={{ padding: "1rem 0" }}>
              <small className="text-gray-400">
                {t("dashboard.noTasksMessage", "No active tasks")}
              </small>
            </div>
          )}

          {/* Task list */}
          {!loading && !error && displayTasks.length > 0 && (
            <ul className="list-group">
              {displayTasks.map((task) => (
                <li
                  key={task.id}
                  className="list-group-item"
                  onClick={() => handleTaskClick(task)}
                  style={{ cursor: task.eventid ? "pointer" : "default" }}
                >
                  {/* Row 1: Title + Priority */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span className="os-task-title">
                      {task.title || task.name || "Untitled Task"}
                    </span>
                    <span
                      className={`badge ${priorityBadgeClass(task.priority)}`}
                      style={{ flexShrink: 0 }}
                    >
                      {(task.priority || "medium").toUpperCase()}
                    </span>
                  </div>

                  {/* Row 2: Due date + Status + Assignee */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "8px",
                      marginTop: "4px",
                    }}
                  >
                    <span className="os-task-date">
                      {formatDateShort(task.taskdate || task.dueDate)}
                      {canSeeAllTasks && task.assignedToName && (
                        <span style={{ marginLeft: 6, opacity: 0.7 }}>
                          &middot; {task.assignedToName}
                        </span>
                      )}
                    </span>
                    <span
                      className={`badge ${statusBadgeClass(task.status)}`}
                      style={{ flexShrink: 0 }}
                    >
                      {(task.status || "pending").toUpperCase()}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* "View All" button — matches ScheduledPostsList / ScheduledEventsList pattern */}
          {!loading && !error && tasks.length > 0 && (
            <div className="text-center mb-3" style={{ marginTop: "8px" }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => navigate(`/${tenant || effectiveTenantId}/tasks`)}
              >
                <FaEye className="mr-1" />
                VIEW ALL TASKS
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Standalone render (future use) ──
  return (
    <div className="dashboard-tasklist-theme">
      <div style={{ padding: "12px" }}>
        <h5>My Tasks</h5>
        {loading && <p>Loading...</p>}
        {error && <p style={{ color: "red" }}>{error}</p>}
        {!loading && !error && tasks.length === 0 && <p>No active tasks</p>}
        {!loading && !error && tasks.length > 0 && (
          <ul className="list-group">
            {tasks.map((task) => (
              <li
                key={task.id}
                className="list-group-item"
                onClick={() => handleTaskClick(task)}
                style={{ cursor: task.eventid ? "pointer" : "default" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong>{task.title || task.name}</strong>
                  <span className={`badge ${statusBadgeClass(task.status)}`}>
                    {(task.status || "pending").toUpperCase()}
                  </span>
                </div>
                <small className="text-gray-400">
                  {formatDateShort(task.taskdate || task.dueDate)}
                  {task.assignedToName && ` \u00b7 ${task.assignedToName}`}
                </small>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
